import { ReactNode, useEffect, useState } from "react";
import {
    App,
    DatePicker,
    Form,
    InputNumber,
    ModalProps,
    Spin,
    Flex,
    Button,
    Input,
    Splitter
} from "antd";
import { useTranslation } from "react-i18next";
import { SupplierApprovePrice, SupplierApprovePriceStateEnum, SupplierPriceAdd } from "@/types/purchase";
import { ProductSelect } from "@/components/Selector";
import useDataDetail from "@/hooks/useDataDetail";
import { CurrencySelect } from "@/components/Selector";
import dayjs from "dayjs";
import ProductDesc from "@/components/Desc/ProductDesc";
import { priceFormat } from "@/utils/format";
import { Product } from "@/types/product";
import { supplierPriceRequestFetch } from "@/apis/supplier";
import DetailModal from "@/components/DetailModal";
import TabsDoc from "../TabsDoc";
import { DocsEnum } from "@/types/doc";
import { InfoWrap } from "./style";


interface PriceFormProps extends ModalProps {
    handleCancel: () => void;
    handleOk: (data: SupplierPriceAdd) => Promise<void>;
    id: string;
    type: "add" | "update" | 'examine';
    leftTitle: ReactNode;
    confirmLoading: boolean;
    propProductList?: Product[];
    btnList?:()=> ReactNode;
    changeType?: () => void;
    supplierInfo?: any;
}

const SupplierPriceForm = ({
    id,
    type,
    leftTitle,
    handleOk,
    handleCancel,
    propProductList,
    btnList,
    changeType,
    supplierInfo,
    ...props
}: PriceFormProps) => {
    const { t } = useTranslation();
    const [form] = Form.useForm<SupplierPriceAdd>();
    const { notification, modal } = App.useApp();
    const [isRead, setIsRead] = useState<boolean>(false)
    const [newDocId, setNewDocId] = useState<string>('')
    const [newPicDocId, setNewPicDocId] = useState<string>('')
    const {
        loading: deLoadingSupp,
        fetchDetail: fetchSupp,
        detail: detailSupp,
    } = useDataDetail<SupplierApprovePrice, [string]>({ api: supplierPriceRequestFetch });
    useEffect(() => {
        
        (type === "update" || type === "examine") &&
            id &&
            fetchSupp(id).then((res) => {
                if (res.approveState !== null &&
                    ([SupplierApprovePriceStateEnum.RESOLVED, SupplierApprovePriceStateEnum.REJECTED].includes(res.approveState)
                        || res.approveState < Date.now()
                    )) setIsRead(true)
                if (res.approveState === null && res.expiredAt < Date.now()) setIsRead(true)
                form.setFieldsValue({
                    ...res,
                    ladders: res.ladders?.length > 1 ? res.ladders?.splice(1) : [],
                    code: res.supplier.code,
                    name: res.supplier.name,
                })
            });
        // @ts-ignore
        (type === 'add') && supplierInfo && supplierInfo.currency && form.setFieldsValue({currency: supplierInfo.currency})
        
    }, [propProductList]);

    useEffect(() => {
        if (propProductList && propProductList.length > 0) {
            form.setFieldValue("productId", propProductList[0].id)
        }
    }, [propProductList])

    const handleFinish = () => {
        form
            .validateFields()
            .then((res) => {
                modal.confirm({
                    title: t('supplierQuotation.confirmQuotate'),
                    content: t('supplierQuotation.confirmQuotate'),
                    onOk: () => {
                        let value: SupplierPriceAdd = {
                            ...res,
                            price: res.price.toString(),
                            expiredAt: res.expiredAt
                                ? dayjs(res.expiredAt).startOf("day").valueOf()
                                : 0,
                            ladders: [],
                        };
                        if (type === 'add' && !value.docId) value.docId = newDocId
                        if (type === 'add' && !value.picDocId) value.picDocId = newPicDocId
                        handleOk(value).then(() => {
                            form.resetFields();
                        });
                    }
                })
            })
            .catch((err) => {
                const errTitle = t("common.addError");
                const errorDesc = err?.errorFields[0]?.errors[0] ?? "";
                notification.error({ placement: 'top',
                    message: errTitle,
                    description: errorDesc,
                });
            });
    };


    const onCancel = () => {
        form.resetFields();
        handleCancel();
    };

    const rePrecie = () => {
        changeType && changeType();
        form.resetFields();
        setIsRead(false)
        setNewDocId('')
        setNewPicDocId('')
    }

    const updateIds = (id: string, docType: DocsEnum) => {
        if (docType === DocsEnum.DOC) setNewDocId(id)
        if (docType === DocsEnum.PIC) setNewPicDocId(id)
    }

    return (
        <DetailModal logBookId={detailSupp?.logBookId || ''} noLog={type === 'add'}
            modalProps={{
                width: type === 'add' ? '60vw' : '80vw',
                onCancel,
                maskClosable: false,
                title: leftTitle,
                ...props

            }}
        >
            {deLoadingSupp && <div style={{ height: "70vh", display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spin /></div>}
            <InfoWrap>
                {!deLoadingSupp && <Splitter layout="vertical" style={{ height: '100%' }}>
                    <Splitter.Panel defaultSize='50%' min='30%' max='70%' style={{overflow:'auto'}}>
                        <Form<SupplierPriceAdd>
                            form={form}
                            name="priceForm"
                            autoComplete="off"
                            initialValues={{ price: detailSupp?.price ?? '' }}
                            disabled={isRead}
                        >

                            <Form.Item
                                label={t("purchase.product")}
                                hidden={type === "update" || type === 'examine'}
                                name="productId"
                                required
                                rules={[{ required: true, message: t("components.plProduct") }]}
                            >
                                <ProductSelect defaultList={propProductList} disabled />
                            </Form.Item>
                            <Form.Item label={t("purchase.product")} hidden={type === "add"}>
                                <ProductDesc product={detailSupp?.product ?? null} showtype="SUPPLIER" />
                            </Form.Item>
                            <Form.Item
                                label={t("purchase.stPrice")}
                                name="price"
                                required
                                rules={[{ required: true, message: t("purchase.plPrice") }]}
                            >
                                <InputNumber changeOnWheel
                                    formatter={(value) => priceFormat(value || '')}
                                    stringMode
                                    min={0}
                                    precision={10000}
                                    placeholder={type === 'examine' ? '' : t("purchase.plPrice")}
                                    style={{ width: '100%' }}
                                    disabled={type === 'examine' || isRead}
                                />
                            </Form.Item>
                            <Form.Item
                                label={t("purchase.expiredAt")}
                                name="expiredAt"
                                getValueProps={(value) => ({
                                    value: value && dayjs(Number(value)),
                                })}
                                normalize={(value) => value && dayjs(value).valueOf()}
                                rules={[{ required: true, message: t("purchase.plExpiredAt") }]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    placeholder={type === 'examine' ? '' : t("purchase.plExpiredAt")}
                                    disabled={type === 'examine' || isRead}
                                />
                            </Form.Item>
                            <Form.Item label={t("purchase.days")} name="days"
                                rules={[{ required: true, message: t("purchase.plDays") }]}
                            >
                                <InputNumber changeOnWheel disabled={type === 'examine' || isRead} style={{ width: "100%" }} placeholder={type === 'examine' ? '' : t("purchase.plDays")} precision={0} min={0} />
                            </Form.Item>
                            <Form.Item label={t("purchase.mpq")} name="mpq">
                                <InputNumber changeOnWheel disabled={type === 'examine' || isRead} style={{ width: "100%" }} placeholder={(type === 'examine' || isRead) ? '' : t("purchase.plMpq")} precision={0} min={0} stringMode />
                            </Form.Item>
                            <Form.Item name={'currency'} label={t("purchase.currency")}>
                                <CurrencySelect  readOnly={type === 'examine' || isRead} />
                            </Form.Item>
                            {
                                type === 'examine' && 
                                    <>
                                        <Form.Item label={t("purchase.code")} name="code">
                                            <Input disabled={type === 'examine'} style={{ width: "100%" }} placeholder={''} />
                                        </Form.Item>
                                        <Form.Item label={t("purchase.name")} name="name">
                                            <Input disabled={type === 'examine'} style={{ width: "100%" }} placeholder={''} />
                                        </Form.Item>
                                    </>
                            }
                        </Form>
                    </Splitter.Panel>
                    <Splitter.Panel defaultSize='50%' min='30%' max='70%' className="doc-panel">
                        <TabsDoc key={type} isRead={isRead}
                            docId={detailSupp && type === 'update' ? detailSupp.docId : newDocId}
                            picDocId={detailSupp && type === 'update' ? detailSupp.picDocId : newPicDocId}
                            handleNewIds={updateIds}
                        />
                    </Splitter.Panel>
                </Splitter>}
                {
                    type === 'examine' ? (
                        <div>
                            {detailSupp && detailSupp.approveState === null && btnList && btnList()}
                        </div>

                    ):(
                        <div>
                            {!isRead && <Flex justify="flex-end" gap={20} className="btn-container">
                                <Button onClick={onCancel}>{t('common.cancel')}</Button>
                                <Button type="primary" onClick={handleFinish} disabled={isRead}>{t('common.confirm')}</Button>
                            </Flex>}
                            {isRead
                            && detailSupp
                            && (detailSupp.expiredAt < Date.now()
                                || detailSupp.approveState === SupplierApprovePriceStateEnum.REJECTED
                            ) && <Flex justify="flex-end" className="btn-container" ><Button type="primary" onClick={rePrecie}>{t("requirement.rePrice")}</Button></Flex>}
                        </div>
                    )
                }
                
            </InfoWrap>
        </DetailModal >
    );
};

export default SupplierPriceForm;
